/**
 * The Add a plant search, as the server answers it (B121; the owner's rule 1: the backend decides, the app displays).
 *
 * The server (bloom-backend services/plants/plantSearch) decides which rows answer what was typed, how each is named,
 * what it says after its name, and in what order, cut at its Grow setting plants.SEARCH_RESULTS_MAX; it also says which
 * crops answer (the Plant drop-down narrows to them) and, for a bed, which of its growing plants do (the photo tray's
 * drop-down narrows to them). The app sends the words and draws the answer as sent; nothing is matched or ranked here.
 *
 * Endpoint: GET /plants/search?q=&lang=&locationId= → PlantSearchAnswer.
 * Who uses it: shared/components/plant-fields usePlantSearch (PlantIdentityFields, the photo tray's PileCard).
 *
 * @module api/plantSearch
 */

import { apiClient } from './apiClient';

/** One row of the answer: a plant growing in the bed, a crop, a type or a variety. */
export interface PlantSearchRow {
  /** Unique within one answer: "p:<plant>", "c:<crop>", "t:<type>" or "v:<variety>". */
  key: string;
  /** What the row is. */
  kind: 'plant' | 'crop' | 'type' | 'variety';
  /** Its name: "Romaine", "Lettuce", "Sun Gold", or a bed plant's card title. */
  label: string;
  /** Said after it, quieter: a crop's other name, a type's crop, a variety's type; null for none. */
  detail: string | null;
  /** Her own type or variety, marked "(yours)" where it is listed. */
  own: boolean;
  /** The crop choosing it sets. */
  cropId: number | null;
  /** The type choosing it sets (a type or a variety); null for a crop or a bed plant. */
  plantId: number | null;
  /** The variety choosing it sets (a variety's own, a type's "Not sure" or only one); null otherwise. */
  varietyId: number | null;
  /** The bed's plant it chooses (the photo tray); null otherwise. */
  plantObjId: number | null;
}

/** The server's answer to one search. */
export interface PlantSearchAnswer {
  /** What was searched, trimmed, as the server read it. */
  query: string;
  /** The rows, best first, already cut to the server's limit. */
  rows: PlantSearchRow[];
  /** Every crop whose names answer: the Plant drop-down narrows to these. */
  cropIds: number[];
  /** The bed's growing plants that answer (with a bed): the photo tray's drop-down narrows to these. */
  plantObjIds: number[];
}

/**
 * Asks the server which rows answer what was typed.
 *
 * @param query - What was typed, trimmed.
 * @param lang - The app's language, for the names.
 * @param locationId - The bed whose growing plants come first (the photo tray); null for none.
 * @returns The server's answer.
 */
export async function getPlantSearch(query: string, lang: string, locationId: number | null = null): Promise<PlantSearchAnswer> {
  const response = await apiClient.get<PlantSearchAnswer & { success: boolean }>('/plants/search', {
    params: { q: query, lang, ...(locationId != null ? { locationId } : {}) },
  });
  const { query: said, rows, cropIds, plantObjIds } = response.data;
  return { query: said, rows, cropIds, plantObjIds };
}
